import type { PhotoStep } from '@mod/domain';

/**
 * SÖKÜM VE REVİZE FAZLARININ ADIM MODELİ — TEK KAYNAK.
 *
 * ⚠️ NEDEN AYRI DOSYA: bu model 07.08.2026'ya kadar YALNIZ `SahaIsDetay`
 *    içinde yaşıyordu. Masaüstündeki iş detayı aynı fazı tek bir ÖZET KUTUSU
 *    olarak gösteriyordu (ekip · not · başlama · bitiş) ve müşteri haklı
 *    olarak *"söküm işleri sipariş içinde kurulum gibi gözükmüyor"* dedi.
 *
 *    Modeli masaüstüne KOPYALAMAK kolay yoldu ve bu repoda o hatanın bedeli
 *    defalarca ödendi (K1'in çalışan id'si, `capture` kancası, üç dosyada
 *    elle yazılmış metraj çarpımı). Bu yüzden kopyalanmadı: iki ekran da
 *    buradan besleniyor.
 *
 * ⚠️ ASIL KAZANÇ GÖRÜNÜMDE DEĞİL. Masaüstündeki kutu yalnız `sokum` adımının
 *    fotoğrafını gösteriyordu; `bos_alan` GÖRÜNMÜYORDU — oysa boş alan
 *    fotoğrafı sökümün K8 kanıtı ve kapanışın sert geçidi. Yani sökümü
 *    kapatan fotoğraf, işin kendi sayfasında yoktu. Fotoğraf adımı burada
 *    adımın bir ALANI olduğu için böyle bir eksiklik artık yapısal olarak
 *    imkânsız: yeni adım eklemek fotoğrafını da yazmayı zorunlu kılıyor.
 */

export interface FazAdimi {
  /** Adım anahtarı — akordeon açma/kapama için. */
  anahtar: string;
  baslik: string;
  /** Bu adımda çekilen fotoğrafın adımı; yoksa fotoğraf üretmeyen bir adımdır. */
  foto?: PhotoStep;
  /** Adımın kısa açıklaması — masaüstünde alt satır olarak görünür. */
  aciklama?: string;
}

/**
 * SÖKÜM — durum makinesi: atandi → basla → devam → temiz → iade.
 *
 * ⚠️ "Söküm emrini aldım" 03.08.2026'da eklendi: faz doğrudan "başladım" ile
 *    açılıyordu, ekip kendi onayını veremiyor ve söküm iş emri kâğıdının
 *    hangi aşamada isteneceğinin yeri bulunmuyordu (§6.6).
 */
export const SOKUM_ADIMLARI: readonly FazAdimi[] = [
  {
    anahtar: 'aldim',
    baslik: 'Söküm emrini aldım',
    foto: 'sokum_is_emri',
    aciklama: 'Söküm iş emri kâğıdı — yeni kâğıt ya da önceki emirle devam',
  },
  { anahtar: 'basla', baslik: 'Söküme başladım', aciklama: 'Zaman damgası' },
  { anahtar: 'tamam', baslik: 'Söküm tamamlandı', foto: 'sokum' },
  {
    anahtar: 'temiz',
    baslik: 'Alan temizliği — boş alan fotosu',
    foto: 'bos_alan',
    aciklama: 'Söküm sonrası boş alanın fotoğrafı ZORUNLU (K8, sert geçit)',
  },
  {
    anahtar: 'kapat',
    baslik: 'Malzeme → kapat',
    aciklama: 'Depoya iade VEYA bölgede bırak (K12 — hurda ayrı prosestir)',
  },
];

/** REVİZE — durum makinesi: atandi → aldim → basladi → bitti → onay. */
export const REVIZE_ADIMLARI: readonly FazAdimi[] = [
  { anahtar: 'aldim', baslik: 'Revizeyi aldım' },
  { anahtar: 'basla', baslik: 'Revizeye başladım', aciklama: 'Zaman damgası' },
  { anahtar: 'bitti', baslik: 'Revize bitti', foto: 'revize' },
  {
    anahtar: 'onay',
    baslik: 'Şef onayı',
    aciklama: 'K1 — revizeyi yapan kendi revizesini onaylayamaz',
  },
];

/**
 * Fazın durumundan KAÇINCI ADIMDA olduğunu verir.
 *
 * ⚠️ `atandi` = 0 ve bu bilinçli: emir henüz alınmadı. Eski söküm kayıtları
 *    `basla` ve sonrasında bir adım ilerlemiş görünür — doğrusu da budur,
 *    o ekipler emri almıştı.
 */
export function fazSirasi(tip: 'sokum' | 'revize', durum: string | null): number {
  const harita: Record<string, number> =
    tip === 'sokum'
      ? { atandi: 0, basla: 1, devam: 2, temiz: 3, iade: 4 }
      : { atandi: 0, aldim: 1, basladi: 2, bitti: 3, onay: 4 };
  return harita[durum ?? 'atandi'] ?? 0;
}

export function fazAdimlari(tip: string): readonly FazAdimi[] {
  return tip === 'revize' ? REVIZE_ADIMLARI : SOKUM_ADIMLARI;
}

/** Adımın durumu — prototipin dört-durum modeli (`mAcc`). */
export type AdimDurumu = 'done' | 'current' | 'locked';

export function adimDurumu(indeks: number, sira: number): AdimDurumu {
  if (sira > indeks) return 'done';
  if (sira === indeks) return 'current';
  return 'locked';
}
